// Pure parsing of the `ui` directive a Director tool returns (no DOM): src/ui.ts applies it, screens that call a
// tool on a tap (Settings' voice auditions) read the artifacts out of it. Unit-tested in directives.test.ts.
import type { UploadWhat } from "./library";
import { reviewPath } from "./notes";
import type { Artifact, FoldDirective, LibraryScope, StageChoice, StageChoiceKind, StageDirective, StageItem, SheetGroup, UiDirective, UiFlat, UiTyped, UploadSheetDirective } from "./types";

export type Parsed = { kind: "typed"; d: UiTyped } | { kind: "flat"; d: UiFlat };

const TYPED = new Set(["navigate", "open_project", "play_video", "open_checkout", "refresh", "show_artifact", "switch_director_voice", "play", "needs_review", "show_formats", "show_models", "new_video_sheet", "memory_changed", "stage", "upload_sheet", "fold", "highlight", "share_sheet", "playback", "back", "pause_call"]);

// ------------------------------------------------------------------ v3: the catalog, models and memory

/** The Models panel's tabs, in order, and the model step each one lists. */
export const MODEL_TABS = [
  { id: "video", step: "clip", label: "Video" },
  { id: "images", step: "image", label: "Images" },
  { id: "voices", step: "voice", label: "Voices" },
  { id: "music", step: "music", label: "Music" },
  { id: "sfx", step: "sfx", label: "Sound effects" },
] as const;
export type ModelTab = (typeof MODEL_TABS)[number]["id"];

/** Where `show_models` goes: /models on the step's tab ("all" or nothing: the first tab). */
export function modelsPath(step?: string | null): string {
  const tab = MODEL_TABS.find((t) => t.step === step || t.id === step);
  return tab && tab !== MODEL_TABS[0] ? `/models?tab=${tab.id}` : "/models";
}

/**
 * What a show_formats / new_video_sheet directive asks of the new-video sheet: the picker with formats highlighted,
 * or one format's brief. Unknown format ids are dropped (an unknown `format` opens the picker). Null for anything else.
 */
export function newVideoRequest(d: UiTyped, known: readonly string[]): { format?: string; highlight?: string[] } | null {
  if (d.type === "show_formats") {
    const highlight = (Array.isArray(d.highlight) ? d.highlight : []).filter((f) => typeof f === "string" && known.includes(f));
    return highlight.length ? { highlight } : {};
  }
  if (d.type === "new_video_sheet") return typeof d.format === "string" && known.includes(d.format) ? { format: d.format } : {};
  return null;
}

/** Flatten a directive (one, a list, or nested lists) into its known parts; anything malformed is dropped. */
export function parseUi(ui: UiDirective | undefined | null): Parsed[] {
  if (!ui) return [];
  const out: Parsed[] = [];
  const visit = (d: unknown) => {
    if (Array.isArray(d)) return d.forEach(visit);
    if (!d || typeof d !== "object") return;
    const o = d as Record<string, unknown>;
    if (typeof o.type === "string") {
      if (!TYPED.has(o.type)) return;
      if (o.type === "show_artifact" && !isArtifact(o.artifact)) return;
      if (o.type === "switch_director_voice" && (typeof o.voiceId !== "string" || !o.voiceId)) return;
      if (o.type === "play" && (typeof o.url !== "string" || !o.url || typeof o.projectId !== "string")) return;
      if (o.type === "needs_review" && typeof o.projectId !== "string") return;
      // v4 (D7): a highlight needs a target or a place to go (src/highlight.ts checks their shape before using them)
      if (o.type === "highlight" && typeof o.actionId !== "string" && typeof o.selector !== "string" && typeof o.path !== "string") return;
      if (o.type === "share_sheet" && (typeof o.projectId !== "string" || typeof o.version !== "number")) return;
      // v4 (V4-D5): pause / resume only
      if (o.type === "playback" && o.action !== "pause" && o.action !== "resume") return;
      if (o.type === "stage") {
        const st = normStageDirective(o);
        if (st) out.push({ kind: "typed", d: st });
        return;
      }
      if (o.type === "upload_sheet" || o.type === "fold") {
        const x = o.type === "upload_sheet" ? normUploadSheet(o) : normFold(o);
        if (x) out.push({ kind: "typed", d: x });
        return;
      }
      out.push({ kind: "typed", d: o as UiTyped });
    } else out.push({ kind: "flat", d: o as UiFlat });
  };
  visit(ui);
  return out;
}

export function isArtifact(a: unknown): a is Artifact {
  if (!a || typeof a !== "object") return false;
  const o = a as Record<string, unknown>;
  return ["frame", "clip", "audio", "script", "render"].includes(String(o.kind)) && typeof o.title === "string" && typeof o.projectId === "string";
}

/** The artifact cards in a directive, in order. */
export const artifactsOf = (ui: UiDirective | undefined | null): Artifact[] =>
  parseUi(ui).flatMap((p) => (p.kind === "typed" && p.d.type === "show_artifact" ? [p.d.artifact] : []));

/** Everything in a directive except its artifact cards (for screens that show the cards themselves). */
export const withoutArtifacts = (ui: UiDirective | undefined | null): (UiTyped | UiFlat)[] =>
  parseUi(ui)
    .filter((p) => !(p.kind === "typed" && p.d.type === "show_artifact"))
    .map((p) => p.d);

/** For a screen that shows a tap's result itself (Settings' "Hear George"): no cards and no stage push either, so a
 *  sample plays in place instead of opening the Director's stage over the page. */
export const withoutCardsOrStage = (ui: UiDirective | undefined | null): (UiTyped | UiFlat)[] =>
  parseUi(ui)
    .filter((p) => !(p.kind === "typed" && (p.d.type === "show_artifact" || p.d.type === "stage")))
    .map((p) => p.d);

// ------------------------------------------------------------------ v4: the stage directive (spec-v4 section 8.4)

const str = (v: unknown): v is string => typeof v === "string" && v.length > 0;
const optStr = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const LETTERS = ["A", "B", "C", "D"] as const;
const CHOICE_KINDS = new Set(["sheets", "frames", "looks", "music", "voices", "formats", "scripts", "clips", "plans"]);
const MODEL_STEPS = new Set(["image", "clip", "voice", "music", "sfx", "all"]);
const GROUPS = new Set(["angles", "faces", "poses"]);

export const isLibraryScope = (v: unknown): v is LibraryScope => v === "account" || (typeof v === "string" && /^(channel|collection|project):[A-Za-z0-9_-]{1,64}$/.test(v));

/**
 * One stage item, checked and trimmed to what its kind needs; null when it's missing an id it can't do without.
 * Choices keep at most four (A-D, re-lettered in order when the letters are missing or repeated); an audition keeps
 * only real audio cards.
 */
export function normStageItem(v: unknown): StageItem | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const title = optStr(o.title);
  const t = title ? { title } : {};
  switch (o.kind) {
    case "project":
    case "script":
    case "estimate":
      return str(o.projectId) ? { kind: o.kind, projectId: o.projectId, ...t } : null;
    case "collection":
      return str(o.collectionId) ? { kind: "collection", collectionId: o.collectionId, ...t } : null;
    case "character":
      return str(o.characterId) ? { kind: "character", characterId: o.characterId, ...t } : null;
    case "sheet": {
      if (!str(o.characterId)) return null;
      const versions = (Array.isArray(o.versions) ? o.versions : []).filter((n): n is number => Number.isInteger(n) && (n as number) > 0).slice(0, 2);
      return { kind: "sheet", characterId: o.characterId, versions, ...(GROUPS.has(o.group as string) ? { group: o.group as SheetGroup } : {}), ...t };
    }
    case "frame":
    case "clip":
      if (!str(o.projectId) || !str(o.sceneId)) return null;
      return { kind: o.kind, projectId: o.projectId, sceneId: o.sceneId, ...(str(o.assetId) ? { assetId: o.assetId } : {}), ...(str(o.url) ? { url: o.url } : {}), ...(o.kind === "frame" && o.large === true ? { large: true } : {}), ...t };
    case "picture":
      if (!str(o.assetId) || !str(o.url)) return null;
      return { kind: "picture", assetId: o.assetId, url: o.url, ...(str(o.projectId) ? { projectId: o.projectId } : {}), ...(o.large === true ? { large: true } : {}), ...t };
    case "video":
      if (!str(o.projectId)) return null;
      return {
        kind: "video",
        projectId: o.projectId,
        ...(str(o.renderId) ? { renderId: o.renderId } : {}),
        ...(Number.isInteger(o.version) && (o.version as number) > 0 ? { version: o.version as number } : {}),
        ...(str(o.url) ? { url: o.url } : {}),
        ...t,
      };
    case "library":
      return { kind: "library", scope: isLibraryScope(o.scope) ? o.scope : "account", ...t };
    case "models":
      return { kind: "models", ...(MODEL_STEPS.has(o.step as string) ? { step: o.step as NonNullable<Extract<StageItem, { kind: "models" }>["step"]> } : {}), ...t };
    case "formats":
      return { kind: "formats", ...(Array.isArray(o.highlight) ? { highlight: o.highlight.filter(str) } : {}), ...t };
    case "choices": {
      if (!CHOICE_KINDS.has(o.choiceKind as string)) return null;
      const raw = (Array.isArray(o.items) ? o.items : []).filter((c): c is Record<string, unknown> => !!c && typeof c === "object" && str((c as Record<string, unknown>).id)).slice(0, LETTERS.length);
      if (!raw.length) return null;
      const given = raw.map((c) => c.letter);
      const keep = given.every((l, i) => LETTERS.includes(l as "A") && given.indexOf(l) === i);
      const items: StageChoice[] = raw.map((c, i) => ({
        letter: keep ? (c.letter as StageChoice["letter"]) : LETTERS[i]!,
        id: c.id as string,
        title: optStr(c.title) ?? `Option ${keep ? (c.letter as string) : LETTERS[i]}`,
        ...(str(c.url) ? { url: c.url } : {}),
        ...(isArtifact(c.artifact) ? { artifact: c.artifact } : {}),
        // v4 (V4-MC): a comparison candidate's model and price
        ...(str(c.model) ? { model: c.model } : {}),
        ...(typeof c.credits === "number" && Number.isFinite(c.credits) ? { credits: c.credits } : {}),
      }));
      // v4 (V4-MC compare_models): the scene's comparison these choices are
      const cm = o.compare && typeof o.compare === "object" ? (o.compare as Record<string, unknown>) : null;
      const compare = cm && str(cm.projectId) && str(cm.sceneId) && str(cm.id) && (cm.step === "frame" || cm.step === "clip") ? { compare: { projectId: cm.projectId as string, sceneId: cm.sceneId as string, id: cm.id as string, step: cm.step as "frame" | "clip" } } : {};
      return { kind: "choices", choiceKind: o.choiceKind as StageChoiceKind, items, ...(optStr(o.question) ? { question: optStr(o.question) } : {}), ...compare, ...t };
    }
    case "references":
      return str(o.projectId) && str(o.sceneId) ? { kind: "references", projectId: o.projectId, sceneId: o.sceneId, ...t } : null;
    case "suno":
      return str(o.projectId) ? { kind: "suno", projectId: o.projectId, ...t } : null;
    case "showcase": {
      const models = (Array.isArray(o.models) ? o.models : []).filter(str).slice(0, 4) as string[];
      if (!str(o.style) || models.length < 2) return null;
      return { kind: "showcase", style: o.style as string, models, ...(o.step === "frame" ? { step: "frame" as const } : {}), ...t };
    }
    case "audition": {
      const candidates = (Array.isArray(o.candidates) ? o.candidates : []).filter((a): a is Artifact => isArtifact(a) && a.kind === "audio");
      if (!candidates.length) return null;
      return { kind: "audition", for: optStr(o.for) ?? "", candidates, ...(Array.isArray(o.heard) ? { heard: o.heard.filter(str) } : {}), ...t };
    }
    default:
      return null;
  }
}

/** The `stage` directive with its bad items dropped; null when none is left. Mode defaults to "replace". */
export function normStageDirective(o: Record<string, unknown>): StageDirective | null {
  const items = (Array.isArray(o.items) ? o.items : []).map(normStageItem).filter((x): x is StageItem => !!x);
  if (!items.length) return null;
  return {
    type: "stage",
    items,
    mode: o.mode === "push" ? "push" : "replace",
    ...(Number.isInteger(o.focus) ? { focus: o.focus as number } : {}),
    ...(optStr(o.title) ? { title: optStr(o.title) } : {}),
  };
}

/** The stage directives in a tool's `ui`, in order. */
export const stageDirectivesOf = (ui: UiDirective | undefined | null): StageDirective[] =>
  parseUi(ui).flatMap((p) => (p.kind === "typed" && p.d.type === "stage" ? [p.d] : []));

// ------------------------------------------------------------------ v4: the upload sheet and folds (V4-D3, V4-D4)

const UPLOAD_KINDS = new Set(["image", "logo", "music", "sfx", "video"]);

/** add_files' `upload_sheet`, checked: a known scope (else the whole account), kind and origin; null without a type. */
export function normUploadSheet(o: Record<string, unknown>): UploadSheetDirective | null {
  if (o.type !== "upload_sheet") return null;
  const origin = o.origin === "suno" ? "suno" : undefined;
  const projectId = optStr(o.projectId);
  // a Suno song always belongs to one video
  if (origin && !projectId) return null;
  return {
    type: "upload_sheet",
    scope: isLibraryScope(o.scope) ? o.scope : "account",
    ...(optStr(o.scopeName) ? { scopeName: optStr(o.scopeName)!.slice(0, 80) } : {}),
    ...(UPLOAD_KINDS.has(o.kind as string) ? { kind: o.kind as UploadSheetDirective["kind"] } : {}),
    ...(optStr(o.role) ? { role: optStr(o.role) } : {}),
    ...(origin ? { origin } : {}),
    ...(projectId ? { projectId } : {}),
  };
}

/** A `fold` directive, checked: an id like "scene.references" and open true/false. */
export function normFold(o: Record<string, unknown>): FoldDirective | null {
  const id = optStr(o.id);
  if (o.type !== "fold" || !id || !/^[A-Za-z0-9_.:-]{1,80}$/.test(id) || typeof o.open !== "boolean") return null;
  return {
    type: "fold",
    id,
    open: o.open,
    ...(str(o.projectId) ? { projectId: o.projectId } : {}),
    ...(str(o.sceneId) ? { sceneId: o.sceneId } : {}),
    ...(Number.isInteger(o.sceneIndex) && (o.sceneIndex as number) > 0 ? { sceneIndex: o.sceneIndex as number } : {}),
  };
}

const ROLE_PRESET: Record<string, UploadWhat> = {
  product: "product",
  location: "place",
  prop: "prop",
  photo: "person",
  drawing: "drawing",
  song: "song",
  music: "song",
  theme: "theme",
  intro: "intro",
  outro: "outro",
};
const KIND_PRESET: Record<string, UploadWhat> = { image: "picture", logo: "logo", music: "song", sfx: "sfx", video: "clip" };

/** The upload sheet's preset for the api's kind / role (the role wins); null when nothing is known. */
export function uploadPreset(kind?: string | null, role?: string | null): UploadWhat | null {
  return (role && ROLE_PRESET[role]) || (kind && KIND_PRESET[kind]) || null;
}

/** One file only: a logo, an intro or an outro (the sheet's `single`). */
export const uploadSingle = (preset: UploadWhat | null) => preset === "logo" || preset === "intro" || preset === "outro";
/** Where a `navigate` directive goes (v4 V4-D1: channels, collections, characters, a library level, settings, models). */
export function navigatePath(ui: Extract<UiTyped, { type: "navigate" }>): string {
  const enc = encodeURIComponent;
  switch (ui.to) {
    case "home":
      return "/";
    case "project":
      return "projectId" in ui && ui.projectId ? `/p/${enc(ui.projectId)}` : "/";
    case "channel":
      return "channelId" in ui && ui.channelId ? `/c/${enc(ui.channelId)}` : "/";
    case "collection":
      return "collectionId" in ui && ui.collectionId ? `/k/${enc(ui.collectionId)}` : "/";
    case "character":
      if (!("characterId" in ui) || !ui.characterId) return "/";
      if (ui.collectionId) return `/k/${enc(ui.collectionId)}/cast/${enc(ui.characterId)}`;
      if (ui.projectId) return `/p/${enc(ui.projectId)}/cast/${enc(ui.characterId)}`;
      return "/";
    case "library":
      return "scope" in ui && ui.scope && ui.scope !== "account" ? `/library?scope=${enc(ui.scope)}` : "/library";
    // v4 (T5): /p/:id/review?v=&t=&note=&safe=&listen=1
    case "review":
      return "projectId" in ui && ui.projectId ? reviewPath(ui.projectId, { v: ui.version, t: ui.t, note: ui.noteId, safe: ui.safe, listen: ui.listen }) : "/";
    default:
      return `/${ui.to}`;
  }
}
